import {loadScript} from '@/utils/scripts';
import {APP_ID, WORKER_PATH} from '@/services/Pushyme/constants';
import {addToken, changeLang, ChangeTokenProps, removeToken as removeTokenApi} from '@/api/push-app';
import {TypeLang} from '@/store/language/language';
import {isApplication} from '@/utils/app';

const pushyPromise = import('pushy-sdk-web')

class Pushyme {
  private _token: string | null = null
  private _registered = false
  private _registeringPromise: Promise<void> | undefined
  private _unregisteringPromise: Promise<void> | undefined

  constructor(){
    this._register = this._register.bind(this)
    this._unregister = this._unregister.bind(this)
  }
  async init() {
    if (this._registeringPromise) {
      return this._registeringPromise
    }
    if (this._registered || isApplication()) {
      return
    }
    this._registeringPromise = new Promise<void>((resolve, reject) => {
      const url = new URL(WORKER_PATH, location.origin)
      loadScript(url.href)
        .then(() => {
          resolve()
        })
        .catch((e) => {
          reject(e)
        })
    })
    this._registeringPromise.finally(() => {
      this._registeringPromise = undefined
    })
    return this._registeringPromise
  }

  private async _register() {
    try {
      const options = {
        appId: APP_ID,
        serviceWorkerFile: WORKER_PATH,
      }
      const { default: Pushy } = await pushyPromise
      this._token = await Pushy.register(options)
      this._registered = true
    } catch (e) {
      this._checkRegisterError()
      if (e instanceof Error) {
        console.error('Pushy.me registration failed ' + e.message) // eslint-disable-line no-console
      }
    }
  }

  private async _unregister() {
    if (!navigator.serviceWorker) {
      return
    }
    if (this._unregisteringPromise) {
      return this._unregisteringPromise
    }
    if (!this._registered) {
      return
    }
    this._unregisteringPromise = Promise.resolve()
      .then(async() => {
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          await subscription.unsubscribe()
        }
      })
    this._unregisteringPromise.finally(() => {
      this._token = null
      this._registered = false
      this._unregisteringPromise = undefined
    })
    return this._unregisteringPromise
  }

  private _waitClick() {
    document.addEventListener('click', () => {
      this._register()
    })
  }

  private async _checkRegisterError() {
    const worker = await navigator.serviceWorker.ready
    const state = await worker.pushManager.permissionState()
    if (state === 'prompt') {
      this._waitClick()
      return
    }
  }

  private async _changeTokenProps(lang: TypeLang, func: (props: ChangeTokenProps) => Promise<any>) {
    if (!this._registered) {
      await this._register().catch()
    }
    if (this._registeringPromise) {
      await this._registeringPromise.catch()
    }
    if (!this._token) {
      throw new Error('Pushy.me token is empty')
    }
    await func({
      lang,
      token: this._token
    })
  }

  async updateToken(lang: TypeLang) {
    if (isApplication()) {
      return
    }
    await this._changeTokenProps(lang, addToken)
  }

  async changeLang(lang: TypeLang) {
    if (isApplication()) {
      return
    }
    await this._changeTokenProps(lang, changeLang)
  }

  async removeToken() {
    const unregisterPromise = this._unregister()
    if (!this._token) {
      return
    }
    await Promise.all([
      unregisterPromise,
      removeTokenApi(this._token)
    ])
  }
}

const pushyme = new Pushyme()

export default pushyme
